/**
 * Client-side vertical-spread repricing.
 *
 * Mirrors stock_analyzer/services/spreads/pricing.py so the heatmap and the
 * simulation sliders can re-price locally on every settings change — no API
 * call, exactly like the screener's elasticityCalculations.js does for single
 * contracts.
 *
 * Every function reads the per-leg payload the backend attaches as `_legs`,
 * so each leg keeps its own implied volatility (the two strikes sit at
 * different points on the smile).
 */
import { blackScholes } from './blackScholes';
import { getColorForGainPct, getMultiplierColor } from '../theme/heatmapPalette';

const RISK_FREE_RATE = 0.05;

/** Scenario columns offered in the settings panel. */
export const SPREAD_SCENARIO_OPTIONS = [
  -50, -35, -20, -15, -10, -5, -2, 0, 2, 5, 10, 15, 20, 35, 50, 100,
];

/**
 * Default columns. Tighter than the screener's single-option default because a
 * vertical caps out: past the short strike every further move returns the same
 * max profit, so extreme columns carry no extra information.
 */
export const DEFAULT_SPREAD_SCENARIOS = new Set([-20, -10, -5, 0, 5, 10, 20]);

const STORAGE_KEY = 'spreads_heatmap_user_scenarios';

export function getUserSpreadScenarios() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return new Set(DEFAULT_SPREAD_SCENARIOS);
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr) || arr.length === 0) return new Set(DEFAULT_SPREAD_SCENARIOS);
    return new Set(arr.map(Number).filter((n) => !isNaN(n)));
  } catch {
    return new Set(DEFAULT_SPREAD_SCENARIOS);
  }
}

export function saveUserSpreadScenarios(set) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(set)));
  } catch {
    /* noop */
  }
}

export function clearUserSpreadScenarios() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* noop */
  }
}

// ---------------------------------------------------------------------------
// Core pricing
// ---------------------------------------------------------------------------

/**
 * Order two legs into the DEBIT orientation, regardless of who owns the spread.
 * Long the leg nearest the money: lower strike for calls, higher for puts.
 *
 * This matters: for a credit spread the stored long/short roles are reversed,
 * and pricing by role instead of by structure inverts the P&L.
 */
export function canonicalLegs(legs) {
  const optionType = String(legs.option_type || '').toLowerCase();
  const [lower, higher] = [legs.long, legs.short].sort(
    (a, b) => parseFloat(a.strike) - parseFloat(b.strike)
  );
  return optionType === 'put' ? { near: higher, far: lower } : { near: lower, far: higher };
}

/**
 * Value of the spread STRUCTURE at a given underlying price and time remaining.
 * Always within [0, width] — a vertical can't be worth less than nothing or
 * more than the distance between its strikes.
 */
export function spreadValue(S, T, legs) {
  const optionType = String(legs.option_type || '').toLowerCase();
  const { near, far } = canonicalLegs(legs);
  const width = Math.abs(parseFloat(near.strike) - parseFloat(far.strike));
  if (!width) return 0;

  const nearValue = blackScholes(S, parseFloat(near.strike), T, RISK_FREE_RATE, near.iv, optionType);
  const farValue = blackScholes(S, parseFloat(far.strike), T, RISK_FREE_RATE, far.iv, optionType);
  return Math.max(0, Math.min(nearValue - farValue, width));
}

/** Projected P&L per contract ($). Credit spreads profit when it narrows. */
export function spreadPnl(entry, simulatedValue, isCredit) {
  return isCredit
    ? (entry - simulatedValue) * 100
    : (simulatedValue - entry) * 100;
}

/**
 * Underlying price where P&L = 0 at the simulated exit, by bisection.
 * The canonical spread value is monotonic in S, so this is well-behaved.
 * Returns null when the entry sits outside [0, width].
 */
export function findSpreadBreakeven(legs, entry, T) {
  const optionType = String(legs.option_type || '').toLowerCase();
  const { near, far } = canonicalLegs(legs);
  const width = Math.abs(parseFloat(near.strike) - parseFloat(far.strike));
  if (!width || !(entry > 0) || entry >= width) return null;

  const strikes = [parseFloat(near.strike), parseFloat(far.strike)];
  let low = Math.max(Math.min(...strikes) * 0.01, 0.01);
  let high = Math.max(...strikes) * 3;
  const increasing = optionType === 'call';

  for (let i = 0; i < 60; i += 1) {
    const mid = (low + high) / 2;
    const value = spreadValue(mid, T, legs);
    if (Math.abs(value - entry) < 1e-4) return mid;
    if ((value < entry) === increasing) low = mid;
    else high = mid;
  }
  return (low + high) / 2;
}

// ---------------------------------------------------------------------------
// Heatmap
// ---------------------------------------------------------------------------

/**
 * Resolve the holding period for one row given the panel's mode.
 * Mirrors computeHeatmapRow's precedence: percent-of-expiration, then a fixed
 * override, then the row's own engine-chosen value.
 */
function resolveHoldingDays(row, daysToExit, daysToExitPercent) {
  const dte = parseFloat(row['Days Until Expiration']);
  if (daysToExitPercent !== null && daysToExitPercent !== undefined) {
    return Math.max(1, Math.round(dte * daysToExitPercent));
  }
  if (daysToExit !== null && daysToExit !== undefined) return daysToExit;
  return parseFloat(row['Days to Gain']) || Math.max(1, Math.round(dte * 0.35));
}

/**
 * Compute every scenario cell for one spread row.
 *
 * @returns {Object} map of scenarioPct -> {
 *   returnOnRisk, valueGain, pnlDollars, simulatedStock, simulatedValue
 * }
 */
export function computeSpreadHeatmapRow(row, daysToExit, scenarios, daysToExitPercent = null) {
  const legs = row._legs;
  const underlying = parseFloat(row['Underlying Price']);
  const entry = parseFloat(row['Net Entry']);
  const risk = parseFloat(row['Risk']);
  const dte = parseFloat(row['Days Until Expiration']);

  if (!legs || !legs.long || !legs.short) return {};
  if (!underlying || !entry || !risk || isNaN(dte)) return {};

  const isCredit = !!legs.is_credit;
  const holdingDays = resolveHoldingDays(row, daysToExit, daysToExitPercent);
  const T = Math.max((dte - holdingDays) / 365, 0.0001);

  const result = {};
  for (const scenarioPct of scenarios) {
    const simulatedStock = underlying * (1 + scenarioPct / 100);
    if (simulatedStock <= 0) {
      result[scenarioPct] = {
        returnOnRisk: null, valueGain: null, pnlDollars: null,
        simulatedStock: null, simulatedValue: null,
      };
      continue;
    }

    const simulatedValue = spreadValue(simulatedStock, T, legs);
    const pnlDollars = spreadPnl(entry, simulatedValue, isCredit);
    const rawGain = ((simulatedValue - entry) / entry) * 100;

    result[scenarioPct] = {
      returnOnRisk: (pnlDollars / risk) * 100,
      // Signed from the position's perspective: a credit spread gains when the
      // structure it is short gets cheaper.
      valueGain: isCredit ? -rawGain : rawGain,
      pnlDollars,
      simulatedStock,
      simulatedValue,
    };
  }
  return result;
}

/**
 * Recompute a row's simulation fields for the Detail view's settings panel.
 * The spread analogue of recalculateSimulation() in blackScholes.js.
 */
export function recalculateSpreadSimulation(row, holdingDays, underlyingGainPct) {
  const legs = row._legs;
  const underlying = parseFloat(row['Underlying Price']);
  const entry = parseFloat(row['Net Entry']);
  const risk = parseFloat(row['Risk']);
  const dte = parseFloat(row['Days Until Expiration']);
  if (!legs || !underlying || !entry || !risk || isNaN(dte)) return row;

  const isCredit = !!legs.is_credit;
  const T = Math.max((dte - holdingDays) / 365, 0.0001);
  const simulatedStock = underlying * (1 + underlyingGainPct / 100);
  const simulatedValue = spreadValue(simulatedStock, T, legs);
  const pnl = spreadPnl(entry, simulatedValue, isCredit);
  const rawGain = ((simulatedValue - entry) / entry) * 100;
  const returnOnRisk = (pnl / risk) * 100;

  // Annualize off return on risk — the meaningful base for a spread, since the
  // capital committed is the risk, not the notional.
  const annualize = (base, days) => {
    if (!days || days <= 0 || base === null || base === undefined) return 'NA';
    try {
      const value = (Math.pow(1 + base / 100, 365 / days) - 1) * 100;
      return isFinite(value) ? value : 'NA';
    } catch {
      return 'NA';
    }
  };

  return {
    ...row,
    'Days to Gain': holdingDays,
    'Underlying Gain %': underlyingGainPct,
    'Simulated Underlying': simulatedStock,
    'Simulated Spread Value': simulatedValue,
    'Simulated P&L': pnl,
    'Return on Risk %': returnOnRisk,
    'Spread Value Gain %': isCredit ? -rawGain : rawGain,
    'Ann. Return (Target)': annualize(returnOnRisk, holdingDays),
    'Ann. Return (Expiry)': annualize(returnOnRisk, dte),
  };
}

// ---------------------------------------------------------------------------
// Presentation
// ---------------------------------------------------------------------------

/**
 * Cell color. Both modes are percentage returns, so they reuse the shared
 * gain palette that the screener heatmap and scoring builder already use.
 */
export function getSpreadCellColor(value, displayMode) {
  if (displayMode === 'multiplier') return getMultiplierColor(value);
  return getColorForGainPct(value);
}

export function formatReturnOnRisk(value) {
  if (value == null || isNaN(value)) return '--';
  const num = Number(value);
  return `${num.toFixed(1)}%`;
}

export function formatValueGain(value) {
  if (value == null || isNaN(value)) return '--';
  const num = Number(value);
  if (Math.abs(num) < 1000) return `${num.toFixed(1)}%`;
  return `${Math.round(num).toLocaleString('en-US')}%`;
}

export function formatSpreadCell(value, displayMode) {
  return displayMode === 'valueGain'
    ? formatValueGain(value)
    : formatReturnOnRisk(value);
}

/** Column header label, e.g. "+10%" / "0%" / "-20%". */
export function formatScenarioLabel(scenarioPct) {
  if (scenarioPct === 0) return '0%';
  return scenarioPct > 0 ? `+${scenarioPct}%` : `${scenarioPct}%`;
}

export function formatCurrency(value) {
  const num = parseFloat(value);
  if (isNaN(num)) return '--';
  const abs = Math.abs(num);
  if (abs >= 1000) return `$${num.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  return `$${num.toFixed(2)}`;
}
